import axios, {AxiosPromise} from 'axios'
import {envVars} from '@/envVars.ts'
import {z, ZodError, ZodSchema} from 'zod'
import useAuthStore from './features/auth/store/store'

const axiosConfig = {
    baseURL: envVars.VITE_API_ENDPOINT,
    headers: {
        common: {
            Authorization: useAuthStore.getState().accessToken
                ? `Bearer ${useAuthStore.getState().accessToken}`
                : false,
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        post: {
            'Access-Control-Allow-Origin': '*'
        }
    }
}

// create axios custom instance with custom config
const axiosInstance = axios.create(axiosConfig)

const attemptRefresh = async (refreshToken: string) => {
    const url = '/host-services/refresh'
    try {
        const {data} = await axiosInstance.post(url, {token: refreshToken})
        useAuthStore.getState().setAccessToken(data['accessToken'])
        useAuthStore.getState().setRefreshToken(data['refreshToken'])
        return data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            useAuthStore.getState().reset()
        } else {
            console.error(error)
        }
    }
}

axiosInstance.interceptors.request.use(request => {
    const token = useAuthStore.getState().accessToken
    if (token) request.headers['Authorization'] = `Bearer ${token}`
    return request
})

const endpointToIgnore = ['/login']

axiosInstance.interceptors.response.use(
    response => {
        // pass response without change
        return response
    },
    async error => {
        // get error info
        const statusCode = error?.response?.status
        const originalRequest = error.config
        console.log('error', error)
        switch (statusCode) {
            case 401: {
                const refreshToken = useAuthStore.getState().refreshToken
                useAuthStore.getState().setRefreshToken(null)
                if (refreshToken && !originalRequest._retry && !endpointToIgnore.includes(error.config.url)) {
                    originalRequest._retry = true // prevent infinite retry loop
                    await attemptRefresh(refreshToken)
                    return axiosInstance.request(originalRequest)
                } else {
                    useAuthStore.getState().reset()
                    return Promise.reject(error)
                }
            }
            default:
                return Promise.reject(error)
        }
    }
)

export default axiosInstance

/**
 * This function parses the api response using the provided shape and returns the response data or the ZodError if the parse fails
 * */
export const parseAxiosPromise = <T extends ZodSchema>({
    axiosPromise,
    responseShape,
    onZodError
}: {
    axiosPromise: AxiosPromise
    responseShape: T
    onZodError?: (error: ZodError) => void
}): Promise<z.infer<T>> =>
    axiosPromise.then(response => {
        const safeParsedResponseData = responseShape.safeParse(response.data)
        if (!safeParsedResponseData.success) {
            console.error(safeParsedResponseData.error)
            onZodError?.(safeParsedResponseData.error)
            throw new Error(safeParsedResponseData.error.message)
        }

        return response.data
    })
